/**
 * Dispatch, recall and lot balance (F7.8 · F7.9 · F7-B1.b) — uc-core src/modules/pack/controllers:
 *   /dispatches GET ?toOrgId&packLotId&search&dateFrom&dateTo · POST · /:id · /:id/dispatch-note.pdf
 *   /dispatches/:id/corrections POST { packLot, pouches, reason }
 *   /lot-balance GET ?seasonCode&cropId&farmerId&search&flagged · /lots/:id/balance
 *   /recall-export ?code&format=json|csv|pdf · POST /recalls { code, reason, reauthToken }
 *   /pack-lots ?status&hold (released lots to load, recalled lots) · /units/:id/trace-back/samples
 *   /stock/lookup ?code (a code → its unit, for trace back)
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getList, getOne, getPage, toQuery, useConsoleMutation, usePageQuery, type Page, type PageParams } from './core'
import { obj, str } from './mappers'
import {
  mapDispatch,
  mapLotBalance,
  mapPackLotOption,
  mapRecallExport,
  mapRecalledPackLot,
  mapTraceBackSamples,
  seasonsFrom,
  type LotBalanceRow,
  type LotBalanceSeason,
} from '../model/recall/mappers'
import type { DispatchBody } from '../model/recall/dispatchForm'
import { codeForLookup } from '../model/recall/recallForm'

/** Query keys for this area (invalidate by prefix). */
export const rk = {
  all: ['console', 'recall'] as const,
  dispatches: ['console', 'recall', 'dispatches'] as const,
  packLots: ['console', 'recall', 'pack-lots'] as const,
  lotBalance: ['console', 'recall', 'lot-balance'] as const,
  export: ['console', 'recall', 'export'] as const,
  traceBack: ['console', 'recall', 'trace-back'] as const,
}
/** Other areas' pack lot keys (the pack agent's pages) — refreshed after a dispatch or recall. */
const PACK_KEYS = [['console', 'pack'] as const]

// ------------------------------ dispatches ------------------------------

export interface DispatchFilters extends PageParams {
  toOrgId?: string
  packLotId?: string
  search?: string
  dateFrom?: string
  dateTo?: string
}
export function useDispatches(f: DispatchFilters) {
  return usePageQuery([...rk.dispatches, 'list', f], () => getPage('/dispatches', f, mapDispatch))
}
export function useDispatch(id: string | undefined) {
  return useQuery({
    queryKey: [...rk.dispatches, 'one', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/dispatches/${id}`, mapDispatch),
  })
}

/** Released pack lots not on hold, with pouches left at the hall (what may go on a trip). */
export function useReleasedPackLots(enabled = true) {
  return useQuery({
    queryKey: [...rk.packLots, 'released'],
    enabled,
    queryFn: () => getPage('/pack-lots', { status: 'RELEASED', hold: 'NONE', page: 1, limit: 200 }, mapPackLotOption),
    staleTime: 30_000,
  })
}

/** Pack lots that have been released (any hold) — the filter choices on the dispatch list. */
export function usePackLotChoices() {
  return useQuery({
    queryKey: [...rk.packLots, 'choices'],
    queryFn: () => getPage('/pack-lots', { page: 1, limit: 200 }, mapPackLotOption).then((p) => p.rows),
    staleTime: 5 * 60_000,
  })
}

/** `onError` returning true keeps a 422 inline in the dialog. */
export function useCreateDispatch(onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<DispatchBody, Record<string, unknown>>({
    mutationFn: (body) => api.post('/dispatches', body),
    invalidate: [rk.dispatches, rk.packLots, rk.export, ...PACK_KEYS],
    success: (d) => `Dispatch ${str(obj(d).code)} recorded`,
    errorTitle: 'Dispatch not recorded',
    onError,
  })
}

export function useDispatchCorrection(id: string, onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<{ packLot: string; pouches: number; reason: string }, Record<string, unknown>>({
    mutationFn: (body) => api.post(`/dispatches/${id}/corrections`, body),
    invalidate: [rk.dispatches, rk.packLots, rk.export, ...PACK_KEYS],
    success: 'Missed item added',
    errorTitle: 'Item not added',
    onError,
  })
}

// ------------------------------ lot balance ------------------------------

export interface LotBalanceFilters extends PageParams {
  seasonCode?: string
  cropId?: string
  farmerId?: string
  search?: string
  flagged?: boolean
}
export interface LotBalancePage extends Page<LotBalanceRow> {
  seasons: LotBalanceSeason[]
}
async function getLotBalances(f: LotBalanceFilters): Promise<LotBalancePage> {
  const page = await getPage('/lot-balance', f, mapLotBalance)
  return { ...page, seasons: seasonsFrom(page.summary) }
}
export function useLotBalances(f: LotBalanceFilters) {
  return useQuery<LotBalancePage>({
    queryKey: [...rk.lotBalance, 'list', f],
    queryFn: () => getLotBalances(f),
    placeholderData: keepPreviousData,
  })
}
export function useLotBalance(lotId: string | undefined) {
  return useQuery({
    queryKey: [...rk.lotBalance, 'one', lotId],
    enabled: Boolean(lotId),
    queryFn: () => getOne(`/lots/${lotId}/balance`, mapLotBalance),
  })
}

// ------------------------------ mock recall & recall ------------------------------

export function useRecallExport(code: string) {
  return useQuery({
    queryKey: [...rk.export, code],
    enabled: Boolean(code),
    retry: false,
    queryFn: () => getOne('/recall-export', mapRecallExport, { code, format: 'json' }),
  })
}

/** Pack lots on recall hold (there is no recall log endpoint; the hold is the record). */
export function useRecalledPackLots(f: PageParams) {
  return usePageQuery([...rk.packLots, 'recalled', f], () =>
    getPage('/pack-lots', { ...f, hold: 'RECALLED' }, mapRecalledPackLot),
  )
}

export function useRecall() {
  return useConsoleMutation<{ code: string; reason: string; reauthToken: string }, Record<string, unknown>>({
    mutationFn: (body) => api.post('/recalls', body),
    invalidate: [rk.all, ...PACK_KEYS],
    success: (d) => {
      const n = Array.isArray(obj(d).pack_lots) ? (obj(d).pack_lots as unknown[]).length : 0
      return n === 1 ? '1 pack lot recalled' : `${n} pack lots recalled`
    },
    errorTitle: 'Recall not recorded',
  })
}

// ------------------------------ trace back samples ------------------------------

/** A code (batch, pack lot, lot, pouch link) → its unit id via the stock lookup. */
async function unitIdForCode(code: string): Promise<string | null> {
  const rows = await getList('/stock/lookup', { code: codeForLookup(code) }, (r) => ({ id: str(r.id), code: str(r.code) }))
  const want = codeForLookup(code).toUpperCase()
  return (rows.find((r) => r.code.toUpperCase() === want) ?? rows[0])?.id ?? null
}

export class CodeNotFoundError extends Error {}

export function useTraceBackSamples(code: string) {
  return useQuery({
    queryKey: [...rk.traceBack, code],
    enabled: Boolean(code),
    retry: false,
    queryFn: async () => {
      const id = await unitIdForCode(code)
      if (!id) throw new CodeNotFoundError(`${code} is not one of our codes.`)
      return getOne(`/units/${id}/trace-back/samples`, mapTraceBackSamples)
    },
  })
}

/** Query string for the export downloads (exposed for the download buttons). */
export const exportQuery = (code: string, format: 'json' | 'csv' | 'pdf') => toQuery({ code, format })
